<script>
import {reactive} from "vue";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import BaseTable from "@/javascript/views/components/charts/BaseTable.vue";
import Icon from "@/javascript/views/components/Icon.vue";
import Chip from "../inputs/Chip.vue";
import DropdownSelect from "../dropdownSelect.vue";
import LoadingSpinner from "../loadingSpinner.vue";
import Tooltip from "../tooltip.vue";

export default {
  name: "BaseDataTable",
  components: {Tooltip, LoadingSpinner, DropdownSelect, Chip, BaseTable, Icon},
  props: {
    /**
     * Whether the table is loading.
     */
    loading: {
      type: Boolean,
      default: true
    },
    /**
     * Can be an array of strings representing the column names
     * OR an array of objects representing the labels.
     * The objects should contain:
     * - a 'label' property containing the string that should be displayed as the column name
     * - a 'key' property containing the key in the row that contains that column's value
     * - OPTIONAL: a 'format' property that can be either 'money', 'date', or 'tags'
     *   and will format the cell accordingly.
     *   - 'tags' format cell data should be an array of strings or objects representing the individual tags.
     *     - if string, tag will be default brand dark green
     *     - if object, should have 'value' attribute for tag text and 'style' attribute with 'style' being one of:
     *       - 'primary' (light green)
     *       - 'secondary' (dark green)
     *       - 'tertiary' (gray)
     */
    labels: {
      type: Array,
      required: true
    },
    /**
     * Array of objects representing each row with the key being the label/label key and the value being the cell value.
     * If only a particular cell and not an entire column needs to be formatted, you can pass an object with a 'format'
     *  property ('money', 'date', or 'image') and 'value' property as the value.
     * If `clickableRows` is set to true, each row object MUST contain an `id` attribute
     *  that contains the id of the record the row represents.
     */
    rows: {
      type: Array,
      required: true
    },
    /**
     * If true, each row will become clickable and redirect to the record corresponding to the row.
     * Requires row objects to contain an `id` attribute that contains the id of the record the row represents.
     */
    clickableRows: {
      type: Boolean,
      default: false
    },
    /**
     * Just used for overview currently.
     * Will highlight the passed column.
     */
    highlightedColumn: {
      type: Number,
      default: null
    },
    /**
     * Will add an additional final column with a right chevron for each row.
     * Will also show the slot for a button in the table head.
     */
    chevronColumn: {
      type: Boolean,
      default: false
    },
    // props to pass to BaseTable
    /**
     * If true will hide table header.
     */
    hideHeader: {
      type: Boolean,
      default: false
    },
    /**
     * The message to show where there is no data available.
     */
    noDataMessage: {
      type: String,
      default: "Not enough data to report on."
    },
    /**
     * Number of static rows to show in the loading state
     */
    loadingRows: {
      type: Number,
      default: 10
    },
    /**
     * Number of static cells to show in the loading rows
     */
    loadingCells: {
      type: Number,
      default: 2
    },
    /**
     * Object to determine which columns are sorted, based on model store
     * {
     *   key: "name",
     *   order: "asc"
     *  },
     */
    sorting: {
      type: Object,
      required: false
    },
    /**
     * Object to determine which columns are filtered, based on model store
     * {
     *   model_value_key: filter_value
     *  },
     */
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['row-click', 'remove-image', 'sort', 'search', 'filter'],
  setup(props) {
    props = reactive(props);
    return {
      classes: {
        'clickable-rows': props.clickableRows,
        'hide-header': props.hideHeader,
        'base-table': true
      }
    };
  },


  methods: {
    sortColumn(label) {
      this.$emit('sort', label);
    },

    // Determines the icon to show based on the passed sorting object
    getSortIcon(label) {
      if (!label || !label.sort || !this.sorting) return null;
      if (this.sorting.key === label.key || this.sorting.key === label.ransackKey) {
        return this.sorting.order === 'desc' ? 'chevron-up' : 'chevron-down';
      } else return "chevron-down";
    },
    moneyFormat(val) {
      return ApplicationHelper.formatMoney(val);
    },
    dateFormat(val) {
      return ApplicationHelper.formatDate(val, {weekday: false, time: false});
    },
    labelString(label) {
      if (typeof label === 'string') {
        return label;
      } else {
        return label.label || '';
      }
    },
    cellValue(row, label) {
      let value = '';

      // get cell value based on label
      if (typeof label === 'string') {
        value = row[label];
      } else {
        value = row[label.key];
      }

      // set value to blank string if null or undefined
      if (value === null || value === undefined) {
        value = '';
      }

      return value;
    },
    formattedCellValue(row, label) {
      // Check raw value before cellValue converts null to ''
      let rawKey = typeof label === 'string' ? label : label.key;
      let rawValue = row[rawKey];

      // If raw value is null and format is money, show dash
      if ((rawValue === null || rawValue === undefined) && label?.format === 'money') {
        return "—";
      }

      let value = this.cellValue(row, label);

      // if the cell value is an object with the format property
      if (typeof value === 'object' && Object.hasOwn(value, 'format')) {
        if (value.format === 'money') {
          value = this.moneyFormat(value.value);
        } else if (value.format === 'date') {
          value = this.dateFormat(value.value);
        } else if (value.format === 'image') {
          value = value.value;
        } else if (value.format === 'boolean') {
          // Formatting available in class
          let trueVal = value.trueValue ? value.trueValue : "Yes";
          let falseVal = value.falseValue ? value.falseValue : "No";
          value = value.value ? trueVal : falseVal;
        } else if (value.format === 'array') {
          if (value.display) {
            value = value.value.map(value.display);
          } else {
            value = value.value;
          }
        } else if (value.format === 'download-link') {
          return "Download";
        }
      }
      // otherwise default to column formatting
      else if (Object.hasOwn(label, 'format')) {
        if (label.format === 'money') {
          value = this.moneyFormat(value);
        } else if (label.format === 'date') {
          value = this.dateFormat(value);
        }
      }

      return value;
    },

    isBoolCell(row, label) {
      let value = this.cellValue(row, label);
      return value.format === 'boolean';
    },

    isArrayCell(row, label) {
      let value = this.cellValue(row, label);
      return value.format === 'array';
    },
    isImageCell(row, label) {
      let value = this.cellValue(row, label);

      return !!(value && typeof value === 'object' && Object.hasOwn(value, 'format') && value.format === 'image');
    },
    tagClass(tag) {
      let classes = {
        'tag-span': true,
      };
      if (typeof tag === 'object') {
        if (Object.hasOwn(tag, 'style') && Object.hasOwn(tag, 'value')) {
          classes[`tag-span--${tag.style}`] = true;
        }
      }
      if (typeof tag === 'string') {
        classes[`tag--${tag.toLowerCase()}`] = true;
      }

      return classes;
    },
    isDownloadLink(label) {
      return label?.format === 'download-link';
    },
    isLinkCell(row, label) {
      let value = this.cellValue(row, label);
      return value && typeof value === 'object' && value.format === 'link';
    },
    onDropdownSelected(label, option) {
      this.$emit('filter', label, option);
    },
  }
};
</script>

<template>
  <base-table
    :class="classes"
    :loading="loading"
    :has-data="rows?.length > 0"
    :hide-header="hideHeader"
    :no-data-message="noDataMessage"
    :loading-rows="loadingRows"
    :loading-cells="loadingCells"
  >
    <template #tableHeader>
      <tr>
        <th
          v-for="(label, index) in labels"
          :key="index"
          class="font-normal border-b titles"
        >
          <button
            v-if="label.sort"
            class=""
            @click="sortColumn(label)"
          >
            <span> {{ labelString(label) }} </span>
            <span class="ml-2">
              <icon
                v-if="getSortIcon(label)"
                size="10px"
                :icon="getSortIcon(label)"
              />
            </span>
          </button>
          <div
            v-else-if="label.select"
            class="flex-row flex min-w-48"
          >
            <span>{{ labelString(label) }}</span>
            <dropdown-select
              v-model="filters[label.key]"
              :options="label.options"
              placeholder="Filter"
              small-text
              pill-style
              show-clear-button-when-selected
              class="absolute top-[-6px] left-2 mb-4"
              @select="(option) => onDropdownSelected(label, option)"
            />
          </div>
          <div v-else>
            {{ labelString(label) }}
          </div>
          <!--          <input v-if="label.search"-->
          <!--                 class="mt-1 w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"-->
          <!--          />-->
        </th>
        <th
          v-if="chevronColumn"
          class="button-cell border-b"
        >
          <!--
            Slot for button in the table header.
          -->
          <slot />
        </th>
      </tr>
    </template>
    <template #tableBody>
      <tr
        v-for="(row, index) in rows"
        :key="index"
        @click="$emit('row-click', row.slug ? row.slug : row.id)"
      >
        <td
          v-for="(label, i) in labels"
          :key="i"
          class=""
          :class="{
            'text-default_primary_dark': highlightedColumn && i === highlightedColumn,
          }"
        >
          <template
            v-if="label && typeof label === 'object' && Object.hasOwn(label, 'format') && label.format === 'tags'"
          >
            <div class="tag-container">
              <span
                v-for="(tag, x) in cellValue(row, label)"
                :key="x"
                :class="tagClass(tag)"
              >
                {{ (typeof tag === 'object' && Object.hasOwn(tag, 'value')) ? tag.value : tag }}
              </span>
            </div>
          </template>
          <template v-else-if="isImageCell(row, label)">
            <div
              class="image-cell-container"
              style="position: relative;"
            >
              <img
                v-if="row.data.value"
                :src="formattedCellValue(row, label)"
                alt="Banner Image"
                class="max-h-[300px] max-w-[300px]"
              >
              <div v-else>
                Not Set
              </div>
            </div>
          </template>
          <template
            v-else-if="isArrayCell(row, label)"
          >
            <div
              class="flex flex-row gap-3"
            >
              <div
                v-for="arrayValue in formattedCellValue(row, label)"
              >
                <chip
                  size="large"
                >
                  <div class="text-md font-semibold">
                    {{ arrayValue }}
                  </div>
                </chip>
              </div>
            </div>
          </template>
          <template
            v-else-if="isBoolCell(row, label)"
          >
            <div
              class="chip chip-bool"
              :class="row.data?.value ? 'chip-true' : 'chip-false'"
            >
              {{ formattedCellValue(row, label) }}
            </div>
          </template>

          <!--     Used for linking household to donors - clickable -->
          <template v-else-if="isLinkCell(row, label)">
            <router-link
              :to="cellValue(row, label).route"
              class="text-blue-600 "
              @click.stop
            >
              {{ cellValue(row, label).value }}
            </router-link>
          </template>
          <template
            v-else-if="isDownloadLink(label)"
          >
            <!--     Error Icon and Tooltip  -->
            <div
              v-if="label.error?.(row)"
              class="w-full flex flex-row justify-start ml-1"
            >
              <tooltip
                variant="warning"
                color="red"
                size="25px"
                tooltip-class="error-tooltip"
              >
                <template #message-slot>
                  Unable to generate report.<br>Try again or contact support.
                </template>
              </tooltip>
            </div>
            <div
              v-else-if="label.loading?.(row)"

              class="w-full flex flex-row justify-start ml-1"
            >
              <loading-spinner
                size="icon-large"
              />
            </div>
            <!-- No downloadable document (e.g. a giving-blind user on a restricted report
                 generated with full data). Show a message instead of a broken link. -->
            <div
              v-else-if="!formattedCellValue(row, label)"
              class="w-full flex flex-row justify-start ml-1"
            >
              <tooltip
                variant="warning"
                size="25px"
                tooltip-class="download-blocked-tooltip"
                :position="index <= 1
                    ? { top: '100%', right: '0', left: 'auto', bottom: 'auto', transform: 'none' }
                    : { bottom: '100%', right: '0', left: 'auto', top: 'auto', transform: 'none' }"
              >
                <template #message-slot>
                  You can't download this report because giving visibility is restricted for your account. Generate a new one.
                </template>
              </tooltip>
            </div>
            <a
              v-else
              :href="formattedCellValue(row, label)"
              download
            >
              <icon
                icon="cloud-arrow-down"
                color="#3a4473"
                size="32px"
                class="cursor-pointer"
              />
            </a>
          </template>
          <!-- Multiline text — preserve entered line breaks (opt-in via format: 'multiline') -->
          <template v-else-if="label && typeof label === 'object' && label.format === 'multiline' || (row && row.format === 'multiline')">
            <div style="white-space: pre-line;">{{ formattedCellValue(row, label) }}</div>
          </template>

          <template v-else>
            {{ formattedCellValue(row, label) }}
            <tooltip
              v-if="row.tooltip && cellValue(row, label) === row.label"
              :message="row.tooltip"
              color="#6b7280"
              size="12px"
              class="ml-1 inline-block"
            />
          </template>
        </td>
        <td v-if="chevronColumn">
          <icon
            class="right-chevron-icon"
            icon="chevron-right"
            size="10px"
          />
        </td>
      </tr>
    </template>
  </base-table>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.clickable-rows {
  tbody tr {
    &:hover {
      cursor: pointer;

      td {
        @apply bg-gray-200;
      }
    }

    td {
      padding: $space-xl $space-md;
    }
  }
}

//.chip {
//  @apply inline-block px-2 py-1 bg-default_secondary text-white rounded-full font-semibold text-sm;
//
//  &.chip-bool {
//
//    &.chip-true {
//      @apply bg-default_primary;
//    }
//    &.chip-false {
//      @apply bg-red-700;
//    }
//  }
//}
.button-cell {
  width: 0;
}

.right-chevron-icon {
  display: block;
  margin: 0 auto;
}

.tag-container {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: $space-xs;

  .tag-span {

    padding: 0 $space-sm;
    border-radius: $radius-sm;
    color: white;
    @apply bg-green-700;

    &.tag-span--primary {
      @apply bg-default_primary;
    }

    &.tag-span--tertiary {
      @apply bg-gray-200;
    }

    &.tag-span--hidden {
      @apply bg-gray-500;
    }

    &.tag--abandoned, &.tag--created {
      @apply bg-gray-300;
    }

    &.tag--error {
      @apply bg-red-800;
    }
  }
}

.titles {
  font-size: 10px;
  color: rgb(100 116 139 / var(--tw-text-opacity, 1));
}

:deep(.error-tooltip) {
  width: 200px !important;
  max-width: 250px !important;
}


</style>
